import React, { useMemo, useState } from 'react';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, KpiCard, ChartCard, RangeControl, ExportMenu, ReportHeader, ReportSkeleton, downloadCsv, exportPdf, tooltipStyle, CARD } from '../components/intelligence/ReportKit';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Anchor } from 'lucide-react';

export default function AssetEarningsReport() {
  const [months, setMonths] = useState(12);
  const { data, loading } = useIntelligenceReport('asset-earnings', months);
  const { formatMoney } = useOperatorLocale();

  const chartData = useMemo(() => (data?.fleetMonthly || []).map((m) => ({
    month: m.month.slice(5) + '/' + m.month.slice(2, 4),
    revenue: m.revenue,
  })), [data]);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv([
      ['Asset', 'Type', 'Year', 'Make', 'Model', 'Revenue', 'Bookings', 'Booked Hours', 'Revenue / Hour', 'Top Service'],
      ...data.assets.map((a) => [
        a.name, a.assetType || '', a.year || '', a.make || '', a.model || '',
        a.revenue, a.bookings, a.bookedHours, a.revenuePerHour, a.topServices[0]?.name || '',
      ]),
    ], `asset-earnings-${data.periodStart}-to-${data.periodEnd}.csv`);
  };

  const s = data?.summary;

  const exportPdfDoc = () => {
    if (!data) return;
    exportPdf({
      filename: `asset-earnings-${data.periodStart}-to-${data.periodEnd}.pdf`,
      title: 'Asset Earnings',
      subtitle: `Your bookings only · ${data.periodStart} to ${data.periodEnd}`,
      kpis: [
        ['Fleet revenue', formatMoney(s.totalRevenue)],
        ['Bookings', String(s.totalBookings)],
        ['Revenue / guest', formatMoney(s.revenuePerGuest)],
        ['Revenue / hour', formatMoney(s.revenuePerHour)],
        ['Earning assets', `${s.activeAssets} / ${s.totalAssets}`],
      ],
      tables: [{
        title: 'Assets',
        head: ['Asset', 'Revenue', 'Bookings', 'Hours', 'Rev/hr', 'Rev/guest', 'Load %', 'Top service'],
        rows: data.assets.map((a) => [
          a.name, formatMoney(a.revenue), a.bookings, a.bookedHours,
          a.revenuePerHour ? formatMoney(a.revenuePerHour) : '-',
          a.revenuePerGuest ? formatMoney(a.revenuePerGuest) : '-',
          a.loadFactor ?? '-', a.topServices[0]?.name || '-',
        ]),
      }],
    });
  };

  return (
    <IntelligenceShell>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="asset-earnings-report">
        <ReportHeader
          title="Asset Earnings"
          blurb={`Every asset in your fleet, ranked by revenue from your own bookings over the last ${months} months. Cancellations and no-shows excluded.`}
          right={<><AiSuggestion report="asset-earnings" months={months} /><RangeControl months={months} setMonths={setMonths} prefix="asset-earnings" /><ExportMenu onCsv={exportCsv} onPdf={exportPdfDoc} testid="asset-earnings-export" /></>}
        />

        {loading ? <ReportSkeleton /> : !data ? null : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
              <KpiCard delay={60} testid="kpi-total-revenue" label="Fleet revenue" value={formatMoney(s.totalRevenue)} sub={`${s.totalBookings} bookings · last ${data.periodMonths} months`} />
              <KpiCard delay={120} testid="kpi-rev-per-guest" label="Revenue / guest" value={formatMoney(s.revenuePerGuest)} sub={`${s.totalGuests.toLocaleString()} guests carried${s.avgLoadFactor != null ? ` · ${s.avgLoadFactor}% avg load` : ''}`} />
              <KpiCard delay={180} testid="kpi-rev-per-hour" label="Revenue / hour" value={formatMoney(s.revenuePerHour)} sub={`${s.bookedHours.toLocaleString()} booked hours`} />
              <KpiCard delay={240} testid="kpi-active-assets" label="Earning assets" value={`${s.activeAssets} / ${s.totalAssets}`} sub={s.unattributedRevenue > 0 ? `${formatMoney(s.unattributedRevenue)} unattributed` : 'all revenue attributed'} />
            </div>

            <ChartCard delay={300} title="Monthly fleet revenue" testid="asset-earnings-chart" className="mb-6">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="intelRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.16} />
                        <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                    <Tooltip formatter={(v) => [formatMoney(v), 'Revenue']} contentStyle={tooltipStyle} />
                    <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} fill="url(#intelRev)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            {data.assets.length === 0 ? (
              <div className={`${CARD} p-12 text-center intel-rise`} data-testid="asset-earnings-empty">
                <div className="w-12 h-12 rounded-full bg-[#EBF2FF] flex items-center justify-center mx-auto mb-4">
                  <Anchor className="w-5 h-5 text-[#2563EB]" strokeWidth={1.75} />
                </div>
                <div className="font-bold text-slate-800 mb-1">No assets yet</div>
                <p className="text-sm text-slate-400">Add assets to your fleet and this report fills itself from your live bookings.</p>
              </div>
            ) : (
              <div className={`${CARD} overflow-x-auto intel-rise`} style={{ animationDelay: '360ms' }} data-testid="asset-earnings-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left">
                      {['Asset', 'Revenue', 'Bookings', 'Rev / hr', 'Rev / guest', 'Avg load', 'Top service'].map((h) => (
                        <th key={h} className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.assets.map((a) => (
                      <tr key={a.id} className="border-b border-slate-50 last:border-0 hover:bg-[#F7F9FC] transition-colors duration-150" data-testid={`asset-earnings-row-${a.id}`}>
                        <td className="px-6 py-4">
                          <div className="font-bold text-slate-900">{a.name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {[a.year, a.make, a.model].filter(Boolean).join(' ') || a.assetType || '—'}
                            {a.capacity ? ` · ${a.capacity} guests` : ''}
                          </div>
                        </td>
                        <td className="px-6 py-4"><MonoNum className="font-bold text-slate-900">{formatMoney(a.revenue)}</MonoNum></td>
                        <td className="px-6 py-4"><MonoNum className="text-slate-600">{a.bookings}</MonoNum></td>
                        <td className="px-6 py-4"><MonoNum className="text-slate-600">{a.revenuePerHour ? formatMoney(a.revenuePerHour) : '—'}</MonoNum></td>
                        <td className="px-6 py-4"><MonoNum className="text-slate-600">{a.revenuePerGuest ? formatMoney(a.revenuePerGuest) : '—'}</MonoNum></td>
                        <td className="px-6 py-4">
                          {a.loadFactor != null ? (
                            <div>
                              <MonoNum className="text-slate-600">{a.loadFactor}%</MonoNum>
                              <div className="text-[11px] text-slate-400 mt-0.5">{a.avgPartySize} of {a.capacity} seats</div>
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-6 py-4 text-slate-600">{a.topServices[0]?.name || <span className="text-slate-300">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </IntelligenceShell>
  );
}
